import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import useAgentTracker from "../../hooks/useAgentTracker";
import "./summary.css";
import Layout from "../../components/Layout/Layout";

const SummarizingAgent = () => {
  // Get URL query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const modeParam = queryParams.get("mode");

  // Common state
  const [mode, setMode] = useState(
    modeParam === "explain" ? "explain" : "summarize"
  ); // "summarize" or "explain"
  const [content, setContent] = useState("");
  const [summary, setSummary] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Summarize mode state
  const [summaryType, setSummaryType] = useState("brief");

  // Explain mode state
  const [concept, setConcept] = useState("");
  const [topic, setTopic] = useState("");

  const fileInputRef = useRef(null);
  const summaryTextRef = useRef(null);
  const copyTimerRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState(null); // null | "copied" | "selected"
  const navigate = useNavigate();

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  // Normalise any summary/explanation payload shape to plain display text
  const toDisplayText = (value, fallback = "") => {
    if (value == null) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && typeof value.text === "string") {
      return value.text;
    }
    if (typeof value === "object" && typeof value.summary === "string") {
      return value.summary;
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return fallback;
    }
  };

  const getSummaryText = () =>
    summary
      ? toDisplayText(
          summary.summary?.text ??
            (typeof summary.summary === "string" ? summary.summary : null) ??
            summary.text ??
            summary,
          "No summary available"
        )
      : "";

  const handleCopySummary = async () => {
    const summaryText = getSummaryText();
    clearTimeout(copyTimerRef.current);
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopyStatus("copied");
    } catch (err) {
      // Fallback: select the summary text so the user can copy manually
      try {
        const node = summaryTextRef.current;
        if (node) {
          const range = document.createRange();
          range.selectNodeContents(node);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          const ok = document.execCommand && document.execCommand("copy");
          setCopyStatus(ok ? "copied" : "selected");
        } else {
          setCopyStatus("selected");
        }
      } catch {
        setCopyStatus("selected");
      }
    }
    copyTimerRef.current = setTimeout(() => setCopyStatus(null), 2000);
  };

  // Agent tracking
  const sessionIdRef = useRef(null);
  const { startAgentSession, recordInteraction, endAgentSession } =
    useAgentTracker();

  // Cleanup effect to end session when component unmounts
  useEffect(() => {
    return () => {
      if (sessionIdRef.current) {
        endAgentSession(sessionIdRef.current);
        console.log("Summarizing agent session ended:", sessionIdRef.current);
      }
    };
  }, [endAgentSession]);

  // Update URL when mode changes
  useEffect(() => {
    // Update the URL query parameter when mode changes without full navigation
    const url = new URL(window.location.href);
    if (mode === "explain") {
      url.searchParams.set("mode", "explain");
    } else {
      url.searchParams.delete("mode");
    }
    window.history.replaceState({}, "", url.toString());

    // Reset state when mode changes
    setSummary(null);
    setExplanation(null);
    setError(null);
  }, [mode]);

  const handleSubmit = async () => {
    if (mode === "summarize") {
      if (!content.trim()) return;

      setLoading(true);
      setSummary(null);
      setExplanation(null);
      setError(null);

      try {
        // Start agent tracking session
        const trackingSessionId = await startAgentSession(
          "summarizing_agent",
          "General", // subject
          `Text Summarization (${summaryType})` // topic
        );
        sessionIdRef.current = trackingSessionId;
        console.log(
          "Summarizing agent tracking session started:",
          trackingSessionId
        );
        // Use the authenticated API service
        const response = await api.post("/api/agents/summarize/text", {
          content: content,
          summary_type: summaryType,
        });
        console.log("Summary response:", response.data);
        setSummary(response.data);

        // Record the summarization interaction
        if (sessionIdRef.current) {
          try {
            await recordInteraction(
              sessionIdRef.current,
              `Summarize: ${content.substring(0, 100)}${
                content.length > 100 ? "..." : ""
              }`,
              `Summary generated (${summaryType} type)`,
              null, // duration will be calculated by the tracker
              null // no feedback for now
            );
            console.log("Summarization interaction recorded");
          } catch (error) {
            console.error("Failed to record summarization interaction:", error);
          }
        }
      } catch (error) {
        console.error("Summarization error:", error.response || error);
        setError("Failed to generate summary. Please try again later.");
      } finally {
        setLoading(false);
      }
    } else if (mode === "explain") {
      handleExplain();
    }
  };

  const handleExplain = async () => {
    if (!concept.trim()) return;

    setLoading(true);
    setSummary(null);
    setExplanation(null);
    setError(null);

    try {
      // Start agent tracking session for explanation
      const trackingSessionId = await startAgentSession(
        "summarizing_agent",
        topic || "General", // subject
        `Concept Explanation: ${concept}` // topic
      );
      sessionIdRef.current = trackingSessionId;
      console.log(
        "Explanation agent tracking session started:",
        trackingSessionId
      );
      // Use the authenticated API service
      const response = await api.post("/api/agents/explain/concept", {
        concept: concept,
        topic: topic || "general",
        continuous_explanation: true,
      });
      console.log("Explanation response:", response.data);
      setExplanation(response.data);

      // Record the explanation interaction
      if (sessionIdRef.current) {
        try {
          await recordInteraction(
            sessionIdRef.current,
            `Explain concept: ${concept}`,
            `Explanation generated for topic: ${topic || "general"}`,
            null, // duration will be calculated by the tracker
            null // no feedback for now
          );
          console.log("Explanation interaction recorded");
        } catch (error) {
          console.error("Failed to record explanation interaction:", error);
        }
      }
    } catch (error) {
      console.error("Explanation error:", error.response || error);
      setError("Failed to generate explanation. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    processFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      processFile(file);
    }
  };

  const processFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    setSummary(null);
    setError(null);

    try {
      // Use the authenticated API service
      const response = await api.post("/api/agents/summarize/file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("File process response:", response.data);
      if (response.data.content) {
        setContent(response.data.content);
      }
      setSummary(response.data.summary || response.data);
    } catch (error) {
      console.error("File processing error:", error.response || error);
      setError("Failed to process file. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="agent-container">
        <h1 className="agent-title">AI Smart Summarizer and Explainer</h1>
        <p className="agent-description">
          {mode === "summarize"
            ? "Generate a concise summary of any text or document. The summary will capture the main points while reducing the length."
            : "Get detailed explanations of academic concepts in a clear, continuous format for better understanding."}
        </p>

        {/* Mode Selection Tabs */}
        <div className="mode-selector">
          <button
            className={`mode-tab ${mode === "summarize" ? "active" : ""}`}
            onClick={() => setMode("summarize")}
          >
            Summarize
          </button>
          <button
            className={`mode-tab ${mode === "explain" ? "active" : ""}`}
            onClick={() => setMode("explain")}
          >
            Explain Concept
          </button>
        </div>

        <div className="summarizer-content">
          <section className="input-section">
            <h2>{mode === "summarize" ? "Input Text" : "Concept Details"}</h2>

            {mode === "summarize" ? (
              <>
                <div
                  className={`file-upload-area ${
                    dragActive ? "drag-active" : ""
                  }`}
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current.click()}
                >
                  <div className="file-upload-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  </div>

                  <div className="file-upload-text">
                    <p>Drag and drop files here or click to browse</p>
                    <p className="file-types">
                      Supported formats: .txt, .doc, .docx, .pdf
                    </p>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    accept=".txt,.doc,.docx,.pdf"
                    style={{ display: "none" }}
                  />
                </div>

                {selectedFile && (
                  <div className="selected-file">
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">
                      ({Math.round(selectedFile.size / 1024)} KB)
                    </span>
                  </div>
                )}

                <div className="text-separator">
                  <span>OR</span>
                </div>

                <textarea
                  className="content-input"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter or paste your text here..."
                  rows={10}
                />

                <div className="summary-options">
                  <label>
                    <input
                      type="radio"
                      name="summaryType"
                      value="brief"
                      checked={summaryType === "brief"}
                      onChange={() => setSummaryType("brief")}
                    />
                    Brief Summary
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="summaryType"
                      value="detailed"
                      checked={summaryType === "detailed"}
                      onChange={() => setSummaryType("detailed")}
                    />
                    Comprehensive Summary
                  </label>
                </div>

                <button
                  className="summarize-button"
                  onClick={handleSubmit}
                  disabled={loading || !content.trim()}
                >
                  {loading ? "Summarizing..." : "Generate Summary"}
                </button>
              </>
            ) : (
              /* Explanation Mode UI */
              <>
                <div className="form-group">
                  <label htmlFor="concept">Concept to Explain:</label>
                  <input
                    type="text"
                    id="concept"
                    className="form-control"
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    placeholder="e.g., Photosynthesis, Newton's Laws, Quadratic Equations"
                  />
                  <small>Enter the specific concept you need explained</small>
                </div>

                <div className="form-group">
                  <label htmlFor="topic">Subject Area (optional):</label>
                  <input
                    type="text"
                    id="topic"
                    className="form-control"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., Biology, Physics, Mathematics"
                  />
                  <small>
                    Providing a subject area helps generate more relevant
                    explanations
                  </small>
                </div>

                <div className="form-group">
                  <label>Explanation Format:</label>
                  <div className="explanation-format">
                    <div className="format-badge">Continuous explanation</div>
                  </div>
                  <small>
                    Explanations are provided in a clear, continuous format
                  </small>
                </div>

                <button
                  className="summarize-button"
                  onClick={handleExplain}
                  disabled={loading || !concept.trim()}
                >
                  {loading ? "Generating Explanation..." : "Explain Concept"}
                </button>
              </>
            )}
          </section>

          {error && <div className="agent-error">{error}</div>}

          {summary && mode === "summarize" && (
            <section className="summary-section">
              <h2>Summary</h2>
              <div className="summary-content">
                <div className="summary-text" ref={summaryTextRef}>
                  {/* Handle all possible summary response structures */}
                  {getSummaryText()}
                </div>
                {Array.isArray(summary.keyPoints) &&
                  summary.keyPoints.length > 0 && (
                  <div className="key-points">
                    <h3>Key Points</h3>
                    <ul>
                      {summary.keyPoints.map((point, index) => (
                        <li key={index}>{toDisplayText(point)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="summary-actions">
                  <button
                    className="action-button"
                    onClick={() => {
                      // Import the file downloader utility
                      import("../../utils/fileDownloader")
                        .then((module) => {
                          // Check if we have a downloadable file path from the backend
                          if (summary.summary?.download_filename) {
                            // Use the backend download endpoint with the provided filename
                            module.validateAndDownload(
                              summary.summary.download_filename,
                              "Summary",
                              (errorMsg) => setError(errorMsg),
                              setLoading
                            );
                          } else if (summary.summary?.downloadable_summary) {
                            // Extract the filename from the path as fallback
                            const pathParts =
                              summary.summary.downloadable_summary.split(
                                /[\/\\]/
                              );
                            const filename = pathParts[pathParts.length - 1];
                            module.validateAndDownload(
                              filename,
                              "Summary",
                              (errorMsg) => setError(errorMsg),
                              setLoading
                            );
                          } else {
                            // Fallback to client-side blob generation if no downloadable file
                            fallbackDownload();
                          }
                        })
                        .catch((err) => {
                          console.error("Error loading file downloader:", err);
                          setError("Failed to load download utility");
                          // Try fallback download if module load fails
                          fallbackDownload();
                        });

                      // Function for client-side fallback download
                      function fallbackDownload() {
                        // Fallback to client-side blob generation
                        const summaryText = getSummaryText();
                        const blob = new Blob(
                          [
                            typeof summaryText === "string"
                              ? summaryText
                              : JSON.stringify(summaryText, null, 2),
                          ],
                          {
                            type: "text/plain",
                          }
                        );
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `summary_${new Date()
                          .toISOString()
                          .slice(0, 10)}.txt`;
                        link.click();
                        URL.revokeObjectURL(url);
                      }
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download Summary
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={handleCopySummary}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy summary
                  </button>
                  <span
                    className="copy-status"
                    role="status"
                    aria-live="polite"
                  >
                    {copyStatus === "copied" && "Copied ✓"}
                    {copyStatus === "selected" &&
                      "Text selected — press Ctrl+C to copy"}
                  </span>
                </div>
              </div>
            </section>
          )}

          {explanation && mode === "explain" && (
            <section className="explanation-section">
              <h2>Concept Explanation</h2>
              <div className="explanation-content">
                <h3>{explanation.concept || concept}</h3>

                {explanation.explanation ? (
                  <div className="explanation-text">
                    {toDisplayText(
                      explanation.explanation,
                      "No explanation available"
                    ) || "No explanation available"}
                  </div>
                ) : (
                  <div className="explanation-text">
                    <p>
                      Sorry, we couldn't generate an explanation for this
                      concept at the moment. Please try a more specific concept
                      or check your connection.
                    </p>
                  </div>
                )}

                <div className="summary-actions">
                  <button
                    className="action-button"
                    onClick={() => {
                      // Import the file downloader utility
                      import("../../utils/fileDownloader")
                        .then((module) => {
                          // Check if we have a downloadable file path from the backend
                          if (explanation.explanation?.download_filename) {
                            // Use the backend download endpoint with the provided filename
                            module.validateAndDownload(
                              explanation.explanation.download_filename,
                              "Explanation",
                              (errorMsg) => setError(errorMsg),
                              setLoading
                            );
                          } else if (
                            explanation.explanation?.downloadable_explanation
                          ) {
                            // Extract the filename from the path as fallback
                            const pathParts =
                              explanation.explanation.downloadable_explanation.split(
                                /[\/\\]/
                              );
                            const filename = pathParts[pathParts.length - 1];
                            module.validateAndDownload(
                              filename,
                              "Explanation",
                              (errorMsg) => setError(errorMsg),
                              setLoading
                            );
                          } else {
                            // Fallback to client-side blob generation if no downloadable file
                            fallbackExplanationDownload();
                          }
                        })
                        .catch((err) => {
                          console.error("Error loading file downloader:", err);
                          setError("Failed to load download utility");
                          // Try fallback download if module load fails
                          fallbackExplanationDownload();
                        });

                      // Function for client-side fallback download
                      function fallbackExplanationDownload() {
                        // Generate text from explanation
                        const conceptName =
                          explanation.concept || concept || "Concept";
                        let explanationText = `Explanation of ${conceptName}\n\n`;

                        // Handle potentially missing explanation object
                        if (explanation.explanation) {
                          explanationText += toDisplayText(
                            explanation.explanation,
                            "No explanation available"
                          );
                        } else {
                          // Fallback if explanation structure is not as expected
                          explanationText +=
                            "The explanation could not be generated properly. " +
                            "Please try again with a more specific concept.\n\n" +
                            "You can also try refreshing the page or checking your internet connection.";
                        }

                        const blob = new Blob([explanationText], {
                          type: "text/plain",
                        });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `explanation_${conceptName.replace(
                          /\s+/g,
                          "_"
                        )}_${new Date().toISOString().slice(0, 10)}.txt`;
                        link.click();
                        URL.revokeObjectURL(url);
                      }
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download Explanation
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>

        <button
          className="back-button"
          onClick={() => navigate("/student-dashboard")}
        >
          Back to Dashboard
        </button>
      </div>
    </Layout>
  );
};

export default SummarizingAgent;
